/**
 * Generic client-side filtering for any list of items that has a status,
 * a trigger, a timestamp, an optional duration, and some searchable string
 * fields. Used by both the Runs page and the Uploads page.
 *
 * Why client-side: the API already returns up to 200 rows in one shot,
 * and the operator scale is dozens of rows per day. Filtering and sorting
 * in the browser keeps the UX instant and avoids a round-trip for every
 * keystroke. If volume ever grows past a few hundred rows we can push
 * filters into query params; the accessor interface here is shaped so the
 * server can implement the same predicate later.
 */

export type SortMode =
  | "newest"
  | "oldest"
  | "duration-desc"
  | "duration-asc"
  | "status";

export interface FilterState {
  /** Empty = "match any status." Otherwise items whose status is in the set match. */
  statuses: string[];
  /** Empty = "match any trigger." */
  triggers: string[];
  /** Empty = "match any Canvas env." History lists show BOTH envs by
   *  default (tagged per row); this narrows to prod or test on demand. */
  envs: string[];
  /** ISO date string `YYYY-MM-DD` (interpreted as start-of-day UTC), or null. */
  fromDate: string | null;
  /** ISO date string `YYYY-MM-DD` (interpreted as end-of-day UTC), or null. */
  toDate: string | null;
  /** Free-text substring; matched case-insensitively against the accessor's searchable fields. */
  search: string;
  sort: SortMode;
}

export const EMPTY_FILTERS: FilterState = {
  statuses: [],
  triggers: [],
  envs: [],
  fromDate: null,
  toDate: null,
  search: "",
  sort: "newest",
};

/** Canvas env pill options shared by every history page. */
export const CANVAS_ENV_OPTIONS = [
  { value: "prod", label: "Canvas prod" },
  { value: "test", label: "Canvas test" },
];

export function isFiltersEmpty(f: FilterState): boolean {
  return (
    f.statuses.length === 0 &&
    f.triggers.length === 0 &&
    f.envs.length === 0 &&
    f.fromDate === null &&
    f.toDate === null &&
    f.search.trim() === "" &&
    f.sort === "newest"
  );
}

/**
 * Accessor pattern so the library doesn't have to know about Run vs Upload
 * shape. The caller provides functions that extract the comparable values
 * from each item.
 */
export interface Accessors<T> {
  status: (item: T) => string;
  trigger: (item: T) => string;
  /** Canvas env of the item; omit on entities without one (env filter is
   *  then ignored). */
  env?: (item: T) => string | undefined;
  /** ISO timestamp used for date-range and "newest/oldest" sort. */
  date: (item: T) => string;
  duration: (item: T) => number | undefined;
  /** Strings/numbers to substring-match against the free-text search. */
  searchFields: (item: T) => (string | number | null | undefined)[];
}

export function applyFilters<T>(
  items: T[],
  filters: FilterState,
  acc: Accessors<T>,
): T[] {
  const search = filters.search.trim().toLowerCase();
  const fromMs = filters.fromDate
    ? Date.parse(filters.fromDate + "T00:00:00.000Z")
    : null;
  const toMs = filters.toDate
    ? Date.parse(filters.toDate + "T23:59:59.999Z")
    : null;

  const statusSet =
    filters.statuses.length > 0 ? new Set(filters.statuses) : null;
  const triggerSet =
    filters.triggers.length > 0 ? new Set(filters.triggers) : null;
  const envSet = filters.envs.length > 0 ? new Set(filters.envs) : null;

  const filtered = items.filter((item) => {
    if (statusSet && !statusSet.has(acc.status(item))) return false;
    if (triggerSet && !triggerSet.has(acc.trigger(item))) return false;
    if (envSet && acc.env && !envSet.has(acc.env(item) ?? "")) return false;
    if (fromMs !== null || toMs !== null) {
      const t = Date.parse(acc.date(item));
      if (Number.isFinite(t)) {
        if (fromMs !== null && t < fromMs) return false;
        if (toMs !== null && t > toMs) return false;
      }
    }
    if (search) {
      const fields = acc.searchFields(item);
      const hit = fields.some(
        (f) =>
          f !== null &&
          f !== undefined &&
          String(f).toLowerCase().includes(search),
      );
      if (!hit) return false;
    }
    return true;
  });

  return sortItems(filtered, filters.sort, acc);
}

function sortItems<T>(items: T[], mode: SortMode, acc: Accessors<T>): T[] {
  const out = [...items];
  switch (mode) {
    case "newest":
      out.sort((a, b) => acc.date(b).localeCompare(acc.date(a)));
      break;
    case "oldest":
      out.sort((a, b) => acc.date(a).localeCompare(acc.date(b)));
      break;
    case "duration-desc":
      // Undefined durations sort last so the user sees real durations first.
      out.sort((a, b) => (acc.duration(b) ?? -1) - (acc.duration(a) ?? -1));
      break;
    case "duration-asc":
      // Undefined durations sort last too — short-and-known beats unknown.
      out.sort((a, b) => {
        const da = acc.duration(a);
        const db = acc.duration(b);
        if (da === undefined && db === undefined) return 0;
        if (da === undefined) return 1;
        if (db === undefined) return -1;
        return da - db;
      });
      break;
    case "status":
      out.sort((a, b) => {
        const cmp = acc.status(a).localeCompare(acc.status(b));
        // Stable secondary sort: newest first within each status bucket.
        return cmp !== 0 ? cmp : acc.date(b).localeCompare(acc.date(a));
      });
      break;
  }
  return out;
}
